/**
 * TanStack Query hooks for Slide Sense usage and telemetry endpoints.
 */

import { useQuery } from "@tanstack/react-query"

import type { UsageWindow } from "../types"

import { fetchUsageNode, fetchUsageSummary, fetchUsageTree } from "../admin-api"

const STALE_MS = 60_000

/**
 * Fetch aggregated usage KPIs, daily series, and top-N lists.
 */
export function useUsageSummary(window: UsageWindow = "30d", enabled = true) {
  return useQuery({
    queryKey: ["slide-sense", "usage", "summary", window],
    queryFn: () => fetchUsageSummary(window),
    staleTime: STALE_MS,
    enabled,
  })
}

/**
 * Fetch a flat tree-map of usage counts keyed by folder/deck/slide ID.
 */
export function useUsageTree(window: UsageWindow = "30d", enabled = true) {
  return useQuery({
    queryKey: ["slide-sense", "usage", "tree", window],
    queryFn: () => fetchUsageTree(window),
    staleTime: STALE_MS,
    enabled,
  })
}

/**
 * Fetch usage detail for a single folder, deck, or slide node.
 * Queries are disabled until both type and id are provided.
 */
export function useUsageNode(
  type: "folder" | "deck" | "slide" | null,
  id: string | null,
  window: UsageWindow = "30d"
) {
  return useQuery({
    queryKey: ["slide-sense", "usage", "node", type, id, window],
    queryFn: () => fetchUsageNode(type!, id!, window),
    enabled: Boolean(type && id),
    staleTime: STALE_MS,
  })
}
